/**
 * repeatPropagation (issue #166)
 *
 * 繰り返しフレーズ (repeat_groups) 内のバッジ編集/削除を、他の出現箇所の
 * 対応位置へ黙って伝播する純粋関数群。副作用なし・React 非依存。
 *
 * 対応の定義:
 *   - occurrence[j][offset] の beatIndex ↔ occurrence[k][offset] の beatIndex (同じ offset)
 *   - さらに和音内は同 noteIndex 同士を対応させる (beatsToBadges が弦昇順で noteIndex を振るため)
 * manualOwned (個別編集済み) バッジは伝播で上書き/削除しない。
 */
import { Badge, RepeatGroup, RepeatEditUpdates } from '../types/fingeringEditor';

export interface RepeatMembership {
  groupId: string;
  occ: number;
  offset: number;
  /** 同 offset の「他」 occurrence の beatIndex 群 (自分自身は含まない)。 */
  corresponding: number[];
}

/** repeat_groups から beatIndex → 所属情報の索引を作る。伝播先算出とハイライト判定に使う。 */
export function buildRepeatIndex(groups?: RepeatGroup[]): Map<number, RepeatMembership> {
  const map = new Map<number, RepeatMembership>();
  (groups ?? []).forEach((g) => {
    g.occurrences.forEach((occ, occIdx) => {
      occ.forEach((beatIndex, offset) => {
        const corresponding = g.occurrences
          .filter((_, i) => i !== occIdx)
          .map((o) => o[offset])
          .filter((v): v is number => v !== undefined);
        map.set(beatIndex, { groupId: g.id, occ: occIdx, offset, corresponding });
      });
    });
  });
  return map;
}

/** source の beatIndex に対応する「他 occurrence」の beatIndex 群を返す (グループ外は空)。 */
export function correspondingBeatIndexes(
  index: Map<number, RepeatMembership>,
  sourceBeatIndex: number,
): number[] {
  return index.get(sourceBeatIndex)?.corresponding ?? [];
}

// 伝播先の一致判定に使う位置キー (beatIndex + 和音内 noteIndex)。伝播/ハイライトで共通。
function positionKey(beatIndex: number, noteIndex: number | null): string {
  return `${beatIndex}:${noteIndex ?? 0}`;
}

// 選択なし / 対応先なし時に返す共有の空集合。参照を固定して不要な再描画を避ける。
const EMPTY_HIGHLIGHT: ReadonlySet<number> = new Set<number>();

/**
 * 選択中バッジと同グループの対応位置 (同 offset・同 noteIndex) にあるバッジ id 集合を返す。
 * 選択なし / 繰り返し無し / 対応先なしのときは共有の空集合 (常時マーキングはしない = issue #120 方針)。
 */
export function highlightedBadgeIdsFor(
  badges: Badge[],
  selectedIds: ReadonlySet<number>,
  index: Map<number, RepeatMembership>,
): ReadonlySet<number> {
  if (selectedIds.size === 0 || index.size === 0) return EMPTY_HIGHLIGHT;
  const targets = new Set<string>();
  for (const b of badges) {
    if (!selectedIds.has(b.id) || b.beatIndex === null) continue;
    for (const cbi of correspondingBeatIndexes(index, b.beatIndex)) {
      targets.add(positionKey(cbi, b.noteIndex));
    }
  }
  if (targets.size === 0) return EMPTY_HIGHLIGHT;
  const ids = new Set<number>();
  for (const b of badges) {
    if (b.beatIndex === null) continue;
    if (targets.has(positionKey(b.beatIndex, b.noteIndex))) ids.add(b.id);
  }
  return ids;
}

/**
 * source バッジの編集内容を、対応位置 (同 offset・同 noteIndex) の他出現へ伝播する。
 * manualOwned は除外。変更したバッジには propagated:true / status:'edited' を付与。
 * @returns badges 適用後 / count 実際に変わった他出現数
 */
export function propagateEdit(
  badges: Badge[],
  sourceId: number,
  updates: RepeatEditUpdates,
  index: Map<number, RepeatMembership>,
): { badges: Badge[]; count: number } {
  const source = badges.find((b) => b.id === sourceId);
  if (!source || source.beatIndex === null) return { badges, count: 0 };
  const membership = index.get(source.beatIndex);
  if (!membership) return { badges, count: 0 };

  const targetBeats = new Set(membership.corresponding);
  const sourceNoteIndex = source.noteIndex;
  let count = 0;
  const next = badges.map((b) => {
    if (
      b.beatIndex !== null &&
      targetBeats.has(b.beatIndex) &&
      b.noteIndex === sourceNoteIndex &&
      !b.manualOwned
    ) {
      count += 1;
      return { ...b, ...updates, propagated: true, status: 'edited' as const };
    }
    return b;
  });
  return { badges: next, count };
}

/**
 * source バッジ群の削除を、対応位置 (同 offset・同 noteIndex) の他出現へ伝播する。
 * manualOwned の対応バッジは削除しない。source 自身はここでは削除しない (呼出し側の責務)。
 * @returns badges 対応削除後 / count 削除した他出現数
 */
export function propagateDelete(
  badges: Badge[],
  sourceIds: number[],
  index: Map<number, RepeatMembership>,
): { badges: Badge[]; count: number } {
  const targets = new Set<string>();
  for (const id of sourceIds) {
    const source = badges.find((b) => b.id === id);
    if (!source || source.beatIndex === null) continue;
    const membership = index.get(source.beatIndex);
    if (!membership) continue;
    for (const cbi of membership.corresponding) {
      targets.add(positionKey(cbi, source.noteIndex));
    }
  }
  if (targets.size === 0) return { badges, count: 0 };

  const idSet = new Set(sourceIds);
  let count = 0;
  const next = badges.filter((b) => {
    if (idSet.has(b.id)) return true; // source 自身は呼出し側が扱う
    if (b.beatIndex === null || b.manualOwned) return true;
    if (targets.has(positionKey(b.beatIndex, b.noteIndex))) {
      count += 1;
      return false;
    }
    return true;
  });
  return { badges: next, count };
}
